import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ShieldCheck, Truck, BadgeIndianRupee, Headphones, Star, ChevronDown, MessageCircle, Sprout, Bug, Shield, Zap, Leaf, FlaskConical, TrendingUp, Wrench, Droplets, SprayCan, Tractor } from "lucide-react";
import api from "@/api/client";
import ProductCard from "@/components/products/ProductCard";
import { useI18n } from "@/contexts/I18nContext";
import { useSiteSettings } from "@/contexts/SiteSettingsContext";
import SeoHead, { faqJsonLd } from "@/components/seo/SeoHead";

const ICONS = { Sprout, Bug, Shield, Zap, Leaf, FlaskConical, TrendingUp, Wrench, Droplets, SprayCan, Tractor };

const Home = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { settings } = useSiteSettings();
  const [q, setQ] = useState("");
  const [cats, setCats] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    Promise.all([
      api.get("/categories"),
      api.get("/products?limit=12"),
      api.get("/sellers/trusted"),
      api.get("/meta/reviews"),
      api.get("/meta/faqs"),
    ]).then(([c, p, s, r, f]) => {
      setCats(c.data);
      setSellers(s.data);
      setReviews(r.data);
      setFaqs(f.data);
      // If admin selected featured products, fetch & use those; else fallback to first 8 by rating
      if (settings.featured_product_ids && settings.featured_product_ids.length > 0) {
        const ids = new Set(settings.featured_product_ids);
        const all = p.data;
        const selected = all.filter((x) => ids.has(x.id));
        const remainder = all.filter((x) => !ids.has(x.id));
        setFeatured([...selected, ...remainder].slice(0, 12));
      } else {
        setFeatured(p.data);
      }
    });
  }, [settings.featured_product_ids]);

  const wa = settings.whatsapp_number || "918090245875";

  const doSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/products?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <div className="bg-background" data-testid="page-home">
      <SeoHead
        title="India's #1 Multi-Vendor Agriculture Marketplace"
        description="Buy genuine seeds, pesticides, fertilizers, irrigation products & farm machinery from GST-verified sellers. Pan-India delivery to 18,000+ pincodes. COD available. Trusted by thousands of farmers."
        path="/"
        jsonLd={faqs.length > 0 ? faqJsonLd(faqs) : null}
      />
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={settings.hero_image} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 hero-vignette" />
        </div>
        <div className="relative container-fah py-16 sm:py-24 lg:py-32">
          <div className="max-w-2xl text-white animate-fade-up">
            <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur border border-white/20 rounded-full px-3 py-1.5 text-xs font-semibold mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-secondary"></span>
              {t(settings.hero_eyebrow)}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] text-balance">
              {t(settings.hero_title_line1)}<br />
              <span className="text-secondary">{t(settings.hero_title_highlight)}</span> {t(settings.hero_title_line2)}
            </h1>
            <p className="mt-5 text-base sm:text-lg text-white/85 max-w-xl">
              {t(settings.hero_subtitle)}
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-white/90">
              <div className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-secondary" /> {t("hero.usp.1")}</div>
              <div className="flex items-center gap-2"><Truck className="w-5 h-5 text-secondary" /> {t("hero.usp.2")}</div>
              <div className="flex items-center gap-2"><BadgeIndianRupee className="w-5 h-5 text-secondary" /> {t("hero.usp.3")}</div>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="container-fah py-12 sm:py-20" data-testid="categories-section">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.15em] text-primary font-semibold mb-2">{t("home.cat.eyebrow")}</div>
            <h2 className="font-display text-2xl sm:text-4xl font-bold">{t("home.cat.title")}</h2>
          </div>
          <Link to="/categories" className="text-sm font-semibold text-primary hover:underline hidden sm:block">{t("home.cat.viewall")}</Link>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4">
          {cats.map((c, i) => {
            const Icon = ICONS[c.icon] || Sprout;
            return (
              <Link key={c.slug} to={`/category/${c.slug}`} className="card-fah hover-lift flex flex-col items-center text-center p-4 sm:p-5 group" data-testid={`category-${c.slug}`}>
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-accent flex items-center justify-center mb-3 group-hover:bg-primary group-hover:text-white transition-colors">
                  <Icon className="w-7 h-7 text-primary group-hover:text-white" />
                </div>
                <div className="text-sm font-semibold leading-tight">{t(c.name)}</div>
                <div className="text-[10px] text-muted-foreground mt-1 line-clamp-2 hidden sm:block">{t(c.description)}</div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="container-fah py-12 sm:py-20" data-testid="featured-section">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.15em] text-primary font-semibold mb-2">{t("home.featured.eyebrow")}</div>
            <h2 className="font-display text-2xl sm:text-4xl font-bold">{t("home.featured.title")}</h2>
          </div>
          <Link to="/products" className="text-sm font-semibold text-primary hover:underline">{t("home.featured.shopall")}</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
          {featured.slice(0, 8).map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>

      {/* WHY US BAND */}
      <section className="bg-primary text-white" data-testid="why-band">
        <div className="container-fah py-10 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { Icon: ShieldCheck, t: "GST Verified Sellers", s: "Every seller hand-verified" },
            { Icon: Truck, t: "18,000+ Pincodes", s: "Fast Pan-India delivery" },
            { Icon: BadgeIndianRupee, t: "COD + Prepaid", s: "Pay your way" },
            { Icon: Headphones, t: "Hindi Support", s: "Talk to us in your language" },
          ].map(({ Icon, t: title, s }, i) => (
            <div key={i} className="flex items-start gap-3" data-testid={`usp-${i}`}>
              <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center shrink-0"><Icon className="w-6 h-6 text-secondary" /></div>
              <div>
                <div className="font-semibold">{t(title)}</div>
                <div className="text-xs text-white/70">{t(s)}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* TRUSTED SELLERS */}
      <section className="bg-muted/40" data-testid="sellers-section">
        <div className="container-fah py-12 sm:py-20">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="text-xs uppercase tracking-[0.15em] text-primary font-semibold mb-2">{t("Verified Network")}</div>
              <h2 className="font-display text-2xl sm:text-4xl font-bold">{t("Trusted Sellers")}</h2>
              <p className="text-muted-foreground mt-2 max-w-xl">{t("Hand-picked, GST-verified agri businesses across India serving thousands of farmers.")}</p>
            </div>
            <Link to="/seller/register" className="hidden sm:inline-block bg-secondary text-secondary-foreground font-semibold px-5 py-2.5 rounded-lg hover:opacity-90" data-testid="cta-become-seller">{t("Become a Seller")}</Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {sellers.map((s) => (
              <div key={s.id} className="card-fah p-5 hover-lift flex gap-4" data-testid={`seller-${s.id}`}>
                <div className="w-14 h-14 rounded-full bg-accent overflow-hidden shrink-0">
                  {s.avatar ? <img src={s.avatar} alt={s.shop_name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-primary font-bold">{s.shop_name?.[0]}</div>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <h3 className="font-semibold truncate">{s.shop_name}</h3>
                    <ShieldCheck className="w-4 h-4 text-primary shrink-0" />
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{t(s.city)}</div>
                  <div className="flex items-center gap-3 mt-1.5 text-xs">
                    <span className="flex items-center gap-0.5"><Star className="w-3.5 h-3.5 fill-secondary text-secondary" /> <span className="font-semibold">{s.rating}</span></span>
                    <span className="text-muted-foreground">{s.total_products} {t("products")}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="container-fah py-12 sm:py-20" data-testid="reviews-section">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="text-xs uppercase tracking-[0.15em] text-primary font-semibold mb-2">{t("From the Farm")}</div>
          <h2 className="font-display text-2xl sm:text-4xl font-bold">{t("Farmers love FarmersAgroHub")}</h2>
          <p className="text-muted-foreground mt-3">{t("Real reviews from real farmers across Bharat.")}</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {reviews.map((r, i) => (
            <div key={i} className="card-fah p-6 hover-lift" data-testid={`review-${i}`}>
              <div className="flex gap-1 text-secondary mb-3">{Array.from({ length: r.rating }).map((_, k) => <Star key={k} className="w-4 h-4 fill-current" />)}</div>
              <p className="text-sm text-foreground/85 leading-relaxed">"{t(r.comment)}"</p>
              <div className="mt-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold">{r.user_name[0]}</div>
                <div>
                  <div className="text-sm font-semibold">{r.user_name}</div>
                  <div className="text-xs text-muted-foreground">{t(r.city)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-muted/30" data-testid="faq-section">
        <div className="container-fah py-12 sm:py-20 max-w-3xl">
          <div className="text-center mb-10">
            <div className="text-xs uppercase tracking-[0.15em] text-primary font-semibold mb-2">{t("Need Help")}</div>
            <h2 className="font-display text-2xl sm:text-4xl font-bold">{t("Frequently Asked Questions")}</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <div key={i} className="card-fah overflow-hidden" data-testid={`faq-${i}`}>
                <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full flex items-center justify-between gap-4 p-5 text-left" data-testid={`faq-toggle-${i}`}>
                  <span className="font-semibold text-sm sm:text-base">{t(f.q)}</span>
                  <ChevronDown className={`w-5 h-5 shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5 text-sm text-muted-foreground leading-relaxed">{t(f.a)}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WhatsApp floating */}
      <a
        href={`https://wa.me/${wa}?text=Hi%2C%20I%20need%20help%20with%20FarmersAgroHub`}
        target="_blank" rel="noreferrer"
        className="fixed bottom-5 right-5 z-40 bg-[#25D366] hover:scale-105 transition text-white rounded-full shadow-xl w-14 h-14 flex items-center justify-center"
        aria-label="Chat on WhatsApp"
        data-testid="whatsapp-floating-btn"
      >
        <MessageCircle className="w-6 h-6" />
      </a>
    </div>
  );
};

export default Home;
